/* eslint arrow-body-style: */
import { batchActions } from 'redux-batched-actions';
import { isCancel, mustRefetch } from 'api/Helpers';
import {
	getProduct as apiGetProduct,
	getProductFamily as apiGetProductFamily,
	getProductOnDisplay as apiGetProductOnDisplay,
	getProductQuantity as apiGetProductQuantity,
	getRelatedProducts as apiGetRelatedProducts,
	getProductReviews as apiGetProductReviews,
} from 'api/Products';
import { getStoreLocation } from 'shared/utils/storeLocationHelpers';

import {
	FETCH_ERROR,
	IS_FETCHING,
	RECEIVE_PRODUCT,
	RECEIVE_PRODUCT_FAMILY,
	RECEIVE_PRODUCT_FAMILY_ON_DISPLAY,
	RECEIVE_PRODUCT_FAMILY_QUANTITY,
	RECEIVE_PRODUCT_ON_DISPLAY,
	RECEIVE_PRODUCT_QUANTITY,
	RECEIVE_PRODUCT_REVIEWS,
	RECEIVE_RELATED_PRODUCTS,
	RECEIVE_PRODUCT_FAMILY_RELATED_PRODUCTS,
	RECEIVE_PRODUCT_FAMILY_REVIEWS,
} from './constants';

/**
 * Store product information in the store with the id as the key.
 * @param {number} productId
 * @param {object} product
 * @param {boolean} cacheable can this product be cached
 */
const receiveProduct = (productId, product, cacheable) => {
	// Only store the last fetch time if the response has marked this data cacheable.
	// An undefined lastFetchTime will always trigger a refetch
	const lastFetchTime = cacheable ? Date.now() : undefined;

	return {
		type: RECEIVE_PRODUCT,
		productId,
		product,
		lastFetchTime,
	};
};

const receiveProductFamily = (productFamilyId, productFamily, productId, cacheable) => {
	const lastFetchTime = cacheable ? Date.now() : undefined;

	return {
		type: RECEIVE_PRODUCT_FAMILY,
		productFamilyId,
		productFamily,
		productId,
		lastFetchTime,
	};
};

const receiveProductFamilyOnDisplay = (productFamilyId, productId, nopWarehouseId, onDisplay) => {
	return {
		type: RECEIVE_PRODUCT_FAMILY_ON_DISPLAY,
		productFamilyId,
		productId,
		nopWarehouseId,
		onDisplay,
	};
};

const receiveProductFamilyQuantity = (productFamilyId, productId, quantity) => {
	return {
		type: RECEIVE_PRODUCT_FAMILY_QUANTITY,
		productFamilyId,
		productId,
		quantity,
	};
};

const receiveProductOnDisplay = (productId, nopWarehouseId, onDisplay) => {
	return {
		type: RECEIVE_PRODUCT_ON_DISPLAY,
		productId,
		nopWarehouseId,
		onDisplay,
	};
};

const receiveProductQuantity = (productId, quantity) => {
	return {
		type: RECEIVE_PRODUCT_QUANTITY,
		productId,
		quantity,
	};
};

const receiveProductReviews = (productId, productReviews) => {
	return {
		type: RECEIVE_PRODUCT_REVIEWS,
		productId,
		productReviews,
	};
};

const receiveRelatedProducts = (productId, relatedProducts) => {
	return {
		type: RECEIVE_RELATED_PRODUCTS,
		productId,
		relatedProducts,
	};
};

const receiveProductFamilyRelatedProducts = (productId, relatedProducts) => {
	return {
		type: RECEIVE_PRODUCT_FAMILY_RELATED_PRODUCTS,
		productId,
		relatedProducts,
	};
};

const receiveProductFamilyReviews = (productId, productReviews) => {
	return {
		type: RECEIVE_PRODUCT_FAMILY_REVIEWS,
		productId,
		productReviews,
	};
};

const updateFetchError = fetchError => ({
	type: FETCH_ERROR,
	fetchError,
});

const updateFetchStatus = isFetching => ({
	type: IS_FETCHING,
	isFetching,
});

/**
 * Fetch details about a specific product.
 * @param {axios cancellation token} cancelToken
 * use api.getCancellationToken() to get a source object.
 * @param {number} productId
 */
const getProduct = (cancelToken, productId) => {
	return (dispatch, getState) => {
		// First check our cache.
		const { product } = getState();
		const lastFetchTime = product.getIn(['products', productId, 'lastFetchTime']);
		if (!mustRefetch(lastFetchTime)) {
			return false;
		}

		dispatch(updateFetchStatus(true));
		// reset so that past product errors don't affect new product fetches
		dispatch(updateFetchError(undefined));

		return apiGetProduct(cancelToken, productId)
			.then(response => {
				dispatch(
					batchActions([
						updateFetchStatus(false),
						receiveProduct(productId, response.data.result.product, response.data.cacheable),
					]),
				);
			})
			.then(() => {
				// This 'then' block will wait until both of the promises below are resolved.
				// Need to return the promise for unit testing
				return Promise.all([
					apiGetRelatedProducts(cancelToken, [productId]).then(relatedProductsResponse => {
						dispatch(
							receiveRelatedProducts(productId, relatedProductsResponse.data.results[productId]),
						);
					}),
					apiGetProductReviews(cancelToken, [productId]).then(productReviewsResponse => {
						dispatch(
							receiveProductReviews(productId, productReviewsResponse.data.results[productId]),
						);
					}),
				]);
			})
			.catch(error => {
				dispatch(updateFetchStatus(false));
				if (isCancel(error)) {
					// Request was canceled.
					return;
				}

				dispatch(updateFetchError(error));
			});
	};
};
/**
 * Fetch details about a product family.
 * @param {axios cancellation token} cancelToken
 * use api.getCancellationTOken() to get a source object.
 * @param {string} productFamilyId
 */
const getProductFamily = (cancelToken, productFamilyId, productId) => {
	return (dispatch, getState) => {
		// First check our cache.
		const { product } = getState();
		const lastFetchTime = product.getIn(['products', productId, 'lastFetchTime']);
		if (!mustRefetch(lastFetchTime)) {
			return false;
		}

		dispatch(updateFetchStatus(true));

		return apiGetProductFamily(cancelToken, productFamilyId)
			.then(response => {
				dispatch(
					batchActions([
						updateFetchStatus(false),
						receiveProductFamily(
							productFamilyId,
							response.data.result.productFamily,
							productId,
							response.data.cacheable,
						),
					]),
				);
				const familyIds = response.data.result.productFamily.products.map(member => member.id);
				return Promise.all([
					apiGetRelatedProducts(cancelToken, familyIds).then(relatedProductsResponse => {
						dispatch(
							receiveProductFamilyRelatedProducts(productId, relatedProductsResponse.data.results),
						);
					}),
					apiGetProductReviews(cancelToken, [productId]).then(productReviewsResponse => {
						dispatch(
							receiveProductFamilyReviews(
								productId,
								productReviewsResponse.data.results[productId],
							),
						);
					}),
				]);
			})
			.catch(error => {
				dispatch(updateFetchStatus(false));
				if (isCancel(error)) {
					// Request was cancelled.
					return;
				}

				dispatch(updateFetchError(error));
			});
	};
};

const getProductFamilyOnDisplay = (cancelToken, productFamilyId, productId) => {
	return dispatch => {
		dispatch(updateFetchStatus(true));
		const nopWarehouseId = getStoreLocation();

		return apiGetProductOnDisplay(cancelToken, productId, nopWarehouseId)
			.then(response => {
				dispatch(
					batchActions([
						updateFetchStatus(false),
						receiveProductFamilyOnDisplay(
							productFamilyId,
							productId,
							nopWarehouseId,
							response.data.onDisplay,
							response.data.cacheable,
						),
					]),
				);
			})
			.catch(error => {
				dispatch(updateFetchStatus(false));
				if (isCancel(error)) {
					// Request was canceled
					return;
				}
				dispatch(updateFetchError(error));
			});
	};
};

const getProductFamilyQuantity = (cancelToken, productFamilyId, productId) => {
	return dispatch => {
		const nopWarehouseId = getStoreLocation();
		dispatch(updateFetchStatus(true));

		return apiGetProductQuantity(cancelToken, productId, nopWarehouseId)
			.then(response => {
				dispatch(
					batchActions([
						updateFetchStatus(false),

						receiveProductFamilyQuantity(
							productFamilyId,
							productId,
							response.data.quantity,
							response.data.cacheable,
						),
					]),
				);
			})
			.catch(error => {
				dispatch(updateFetchStatus(false));
				if (isCancel(error)) {
					// Request was cancelled
					return;
				}
				dispatch(updateFetchError(error));
			});
	};
};

const getProductOnDisplay = (cancelToken, productId) => {
	return dispatch => {
		dispatch(updateFetchStatus(true));
		const nopWarehouseId = getStoreLocation();

		return apiGetProductOnDisplay(cancelToken, productId, nopWarehouseId)
			.then(response => {
				dispatch(
					batchActions([
						updateFetchStatus(false),
						receiveProductOnDisplay(
							productId,
							nopWarehouseId,
							response.data.onDisplay,
							response.data.cacheable,
						),
					]),
				);
			})
			.catch(error => {
				dispatch(updateFetchStatus(false));
				if (isCancel(error)) {
					// Request was canceled
					return;
				}
				dispatch(updateFetchError(error));
			});
	};
};

const getProductQuantity = (cancelToken, productId) => {
	return dispatch => {
		dispatch(updateFetchStatus(true));
		const nopWarehouseId = getStoreLocation();
		return apiGetProductQuantity(cancelToken, productId, nopWarehouseId)
			.then(response => {
				dispatch(
					batchActions([
						updateFetchStatus(false),
						receiveProductQuantity(productId, response.data.quantity, response.data.cacheable),
					]),
				);
			})
			.catch(error => {
				dispatch(updateFetchStatus(false));
				if (isCancel(error)) {
					// Request was cancelled
					return;
				}
				dispatch(updateFetchError(error));
			});
	};
};

export {
	getProduct,
	getProductFamily,
	getProductFamilyOnDisplay,
	getProductFamilyQuantity,
	getProductOnDisplay,
	getProductQuantity,
	receiveProduct,
	receiveProductFamily,
	receiveProductFamilyOnDisplay,
	receiveProductFamilyQuantity,
	receiveProductOnDisplay,
	receiveProductQuantity,
	receiveProductReviews,
	receiveRelatedProducts,
	receiveProductFamilyRelatedProducts,
	receiveProductFamilyReviews,
	updateFetchError,
	updateFetchStatus,
};
